import * as React from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import { CircularProgress, Divider, Icon, Menu, Stack, Typography, useMediaQuery } from '@mui/material';
import AppBar from "./AppBar";
import { MobileAppBar } from './MobileAppBar';
import { createAdministrationItemList, createBiItemList, createChatItemList, createOpsItemList, createServiceItemList, createSettingsRestaurantItemList } from './CreateItemList';
import ListButtonVerticalBar from '../../components_new/ListButtonVerticalBar';
import { MobileVerticalNavBar } from './MobileVerticalNavBar';
import UserAuthenticated from '../../auth/UserAuthenticated';
import { Outlet, useMatch, useNavigate, useParams } from 'react-router-dom-v5-compat';
import { Suspense } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import Fetch from '../../script/Fetch';
import { useEventTracker } from '../../eventtracker/eventTrackerContext';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    border: 0,
    position: "relative",
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

// Custom Drawer styling for desktop mode based on the "open" prop
const DesktopDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        position: "relative",
        '& .MuiPaper-root': {
            paddingRight: 1
        },
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    })
);

const VerticalBar = ({ ristoranteId, routeMatch }: any) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const navigate = useNavigate();
    const { trackEvent } = useEventTracker();

    let administrationItemList = createAdministrationItemList(ristoranteId, navigate);
    let settingsItemList = createSettingsRestaurantItemList(ristoranteId, navigate);
    let opsItemList = createOpsItemList(ristoranteId, navigate);
    let serviceItemList = createServiceItemList(ristoranteId, navigate);
    let biItemList = createBiItemList(ristoranteId, navigate);
    let chatItemList = createChatItemList(ristoranteId, navigate);

    let chat = routeMatch.pathname.includes('chat');
    let myAccount = routeMatch.pathname.includes('myAccount');
    let cassa = routeMatch.pathname.includes('cassa');
    let servizio = routeMatch.pathname.includes('servizio');
    let bi = routeMatch.pathname.includes('bi');
    let settings = routeMatch.pathname.includes('impostazioni');

    let itemList = undefined;
    if (myAccount) {
        itemList = administrationItemList;
    } else if (cassa) {
        itemList = opsItemList;
    } else if (servizio) {
        itemList = serviceItemList;
    } else if (bi) {
        itemList = biItemList;
    } else if (settings) {
        itemList = settingsItemList;
    } else if (chat) {
        itemList = chatItemList;
    }

    if (itemList === undefined) {
        return null;
    }

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    let ristoranteList = UserAuthenticated.getInstance().ristoranteList;

    return (
        <DesktopDrawer variant="permanent" open={true} sx={{
            position: "relative",
            borderRight: "1px solid #ececf1",
        }}>
            <Stack
                aria-describedby="test"
                direction="row"
                sx={theme => ({
                    width: '100%',
                    alignItems: 'center',
                    minHeight: "54px",
                    pl: 3,
                    pr: 3,
                    borderBottom: "1px solid #ececf1",
                    gap: 1,
                    cursor: "pointer",
                    "&:hover": {
                        backgroundColor: theme.palette.action.hover
                    }
                })}
                aria-controls={anchorEl ? 'header-menu' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
            >
                <img
                    alt="Logo del sito"
                    src="/mascotte-bar.png"
                    style={{ height: 24 }}
                />
                {/* <img
                    alt="Logo del sito"
                    src="/logo-iammi.png"
                    style={{ height: 28 }}
                /> */}
                <svg viewBox="0 0 24 24" width="30" height="16" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" fill="none" shape-rendering="geometricPrecision" data-sentry-element="svg" data-sentry-source-file="LayoutHeader.tsx"><path d="M16 3.549L7.12 20.600" data-sentry-element="path" data-sentry-source-file="LayoutHeader.tsx"></path></svg>
                <Typography noWrap width={"100%"}>
                    {
                        UserAuthenticated.getInstance().selectedRistorante?.nome
                    }
                </Typography>
                <UnfoldMoreIcon sx={theme => ({
                    color: theme.palette.text.secondary
                })} />
            </Stack>
            <Menu
                id="header-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                disablePortal                 // renderizza il menu nel contesto del Drawer
                anchorOrigin={{              // punto di aggancio sullo Stack
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{           // punto di origine del menu stesso
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{
                    "& .MuiList-root": {
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        p: 1
                    }
                }}
            >
                {
                    ristoranteList.map((ristorante: any) => {
                        return (
                            <>
                                {
                                    UserAuthenticated.getInstance().selectedRistorante?.id === ristorante.id ? (
                                        <Stack sx={{
                                            px: 1,
                                            py: 0.75,
                                            minWidth: "300px",
                                            color: "rgb(43, 73, 245)",
                                            fontSize: 14,
                                            fontWeight: "bold",
                                            borderRadius: 1,
                                            flexDirection: "row",
                                            justifyContent: "space-between"
                                        }}>
                                            <Stack direction="row" alignItems="center" gap={1}>
                                                <Stack sx={{
                                                    width: 20,
                                                    height: 20,
                                                    backgroundColor: "red",
                                                    borderRadius: 0.5
                                                }}>
                                                    <Box
                                                        component="img"
                                                        src={ristorante.urlLogo}
                                                        alt={`${ristorante.nome} logo`}
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            borderRadius: 'inherit',
                                                        }}
                                                    />
                                                </Stack>
                                                {ristorante.nome}
                                            </Stack>
                                            <CheckCircleIcon fontSize={"small"} />
                                        </Stack>
                                    ) : (
                                        <Stack key={ristorante.id} sx={{
                                            px: 1,
                                            py: 0.75,
                                            minWidth: "230px",
                                            fontSize: 14,
                                            color: "rgb(99, 115, 129)",
                                            "&:hover": {
                                                backgroundColor: "rgb(246, 247, 255)",
                                                color: "rgb(32, 54, 182)",
                                                cursor: "pointer"
                                            }
                                        }}
                                            onClick={() => {
                                                trackEvent("menuButton", { ristorante: ristorante.nome });
                                                navigate(`/bi/${ristorante.id}`);
                                                UserAuthenticated.getInstance().setSelectedRistorante(ristorante.id);
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" gap={1}>
                                                <Stack sx={{
                                                    width: 20,
                                                    height: 20,
                                                    backgroundColor: "red",
                                                    borderRadius: 0.5
                                                }}>
                                                    <Box
                                                        component="img"
                                                        src={ristorante.urlLogo}
                                                        alt={`${ristorante.nome} logo`}
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            borderRadius: 'inherit',
                                                        }}
                                                    />
                                                </Stack>
                                                {ristorante.nome}
                                            </Stack>
                                        </Stack>
                                    )
                                }
                            </>
                        );
                    })
                }

                <Divider />
                <Stack direction="row" alignItems="center" gap={1} sx={{
                    px: 1,
                    py: 0.75,
                    minWidth: "230px",
                    fontSize: 14,
                    color: "rgb(99, 115, 129)",
                    "&:hover": {
                        backgroundColor: "rgb(246, 247, 255)",
                        color: "rgb(32, 54, 182)",
                        cursor: "pointer"
                    }
                }} onClick={() => {
                    Fetch.singOut();
                }}>
                    <LogoutIcon fontSize={"small"} />
                    Esci
                </Stack>
            </Menu>
            <ListButtonVerticalBar lists={itemList} firstSelectedItem={0} />
        </DesktopDrawer >
    );
}

export const VerticalNavBar = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const routeMatch = useMatch('*');
    // get ristoranteId from URL param if present
    const params = useParams<{ ristoranteId: string }>();
    const paramRistoranteId = params.ristoranteId;
    // sync selection in singleton on URL change
    React.useEffect(() => {
        if (paramRistoranteId && paramRistoranteId !== UserAuthenticated.getInstance().selectedRistorante?.id) {
            UserAuthenticated.getInstance().setSelectedRistorante(paramRistoranteId);
        }
    }, [paramRistoranteId]);
    // derive current ristoranteId
    const ristoranteId = paramRistoranteId || UserAuthenticated.getInstance().selectedRistorante?.id || "";
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

    return (
        <Stack direction="row" sx={{
            height: "100%",
        }}>
            {
                !isMobile && <VerticalBar ristoranteId={ristoranteId} routeMatch={routeMatch} />
            }

            <Stack sx={{
                width: "100%"
            }}>
                {
                    !isMobile ? <AppBar ristoranteId={ristoranteId} /> : <MobileAppBar mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
                }
                <Box sx={{
                    width: "100%",
                    height: "100%",
                }}>

                    {
                        isMobile && mobileMenuOpen && <MobileVerticalNavBar isMobileAndOpened={isMobile && mobileMenuOpen} url={routeMatch?.pathname ?? ''} ristoranteId={ristoranteId} onClose={() => setMobileMenuOpen(false)} />
                    }

                    <Box component="main" sx={{
                        flexGrow: 1,
                        transition: theme.transitions.create(['margin'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                        height: "100%",
                        overflow: "hidden",
                        position: "relative",
                        backgroundColor: "#f9fafb"
                    }}>
                        <Stack sx={{
                            overflow: "scroll",
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            top: 0
                        }}>
                            <Suspense fallback={
                                <Stack sx={{
                                    width: '100%',
                                    height: '100%',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}>
                                    <CircularProgress />
                                </Stack>
                            }>
                                <Outlet />
                            </Suspense>
                        </Stack>
                    </Box>
                </Box>
            </Stack>
        </Stack>
    );
}
